import {VelaScene, type VelaContext, type VelaRule} from '../engine/types';

type DepositKind = 'hold' | 'preAuth';
type CoverKind = 'both' | 'deposit' | 'protection';

const WHICH: Record<DepositKind, string> = {
  hold: 'chat.deposit.which.a_hold',
  preAuth: 'chat.deposit.which.a_pre_auth',
};

const TAKEN: Record<DepositKind, {q: string; a: string}> = {
  hold: {q: 'chat.deposit.taken.q_hold', a: 'chat.deposit.taken.a_hold'},
  preAuth: {q: 'chat.deposit.taken.q_pre_auth', a: 'chat.deposit.taken.a_pre_auth'},
};

const BACK: Record<DepositKind, {q: string; a: string}> = {
  hold: {q: 'chat.deposit.back.q_hold', a: 'chat.deposit.back.a_hold'},
  preAuth: {q: 'chat.deposit.back.q_pre_auth', a: 'chat.deposit.back.a_pre_auth'},
};

const FEE: Record<DepositKind, string> = {
  hold: 'chat.deposit.fee.a_hold',
  preAuth: 'chat.deposit.fee.a_pre_auth',
};

const DAMAGE: Record<CoverKind, string> = {
  both: 'chat.deposit.damage.a_both',
  deposit: 'chat.deposit.damage.a_deposit',
  protection: 'chat.deposit.damage.a_protection',
};

const NOTHING_BREAKS: Record<CoverKind, Record<DepositKind, string>> = {
  both: {
    hold: 'chat.deposit.nothing_breaks.a_both_hold',
    preAuth: 'chat.deposit.nothing_breaks.a_both_pre_auth',
  },
  deposit: {
    hold: 'chat.deposit.nothing_breaks.a_deposit_hold',
    preAuth: 'chat.deposit.nothing_breaks.a_deposit_pre_auth',
  },
  protection: {
    hold: 'chat.deposit.nothing_breaks.a_protection',
    preAuth: 'chat.deposit.nothing_breaks.a_protection',
  },
};

function amounts({deposit}: VelaContext) {
  return {
    deposit: deposit.depositAmount,
    protection: deposit.protectionAmount,
    limit: deposit.protectionLimit,
    fee: deposit.depositFee,
  };
}

function hasDepositFee({deposit}: VelaContext) {
  return deposit.hasDeposit && Boolean(deposit.depositFee);
}

function depositKind({deposit}: VelaContext): DepositKind {
  return deposit.isRetention ? 'hold' : 'preAuth';
}

function coverKind({deposit}: VelaContext): CoverKind | null {
  if (deposit.hasDeposit && deposit.hasProtection) return 'both';
  if (deposit.hasDeposit) return 'deposit';
  return deposit.hasProtection ? 'protection' : null;
}

export const depositRules: VelaRule[] = [
  {
    id: 'deposit/ask-which',
    section: 'askVela',
    scenes: [VelaScene.Deposit],
    priority: 100,
    evaluate: (ctx, t) =>
      coverKind(ctx) === 'both'
        ? {
            id: 'ask-which',
            title: t('chat.deposit.which.q'),
            description: t(WHICH[depositKind(ctx)], amounts(ctx)),
          }
        : null,
  },
  {
    id: 'deposit/ask-taken',
    section: 'askVela',
    scenes: [VelaScene.Deposit],
    priority: 90,
    evaluate: (ctx, t) => {
      if (!ctx.deposit.hasDeposit) return null;
      const copy = TAKEN[depositKind(ctx)];
      return {
        id: 'ask-taken',
        title: t(copy.q, amounts(ctx)),
        description: t(copy.a, amounts(ctx)),
      };
    },
  },
  {
    id: 'deposit/ask-back',
    section: 'askVela',
    scenes: [VelaScene.Deposit],
    priority: 80,
    evaluate: (ctx, t) => {
      if (!ctx.deposit.hasDeposit) return null;
      const copy = BACK[depositKind(ctx)];
      return {
        id: 'ask-back',
        title: t(copy.q, amounts(ctx)),
        description: t(copy.a, amounts(ctx)),
      };
    },
  },
  {
    id: 'deposit/ask-damage',
    section: 'askVela',
    scenes: [VelaScene.Deposit],
    priority: 70,
    evaluate: (ctx, t) => {
      const cover = coverKind(ctx);
      if (!cover) return null;
      return {
        id: 'ask-damage',
        title: t('chat.deposit.damage.q'),
        description: t(DAMAGE[cover], amounts(ctx)),
      };
    },
  },
  {
    id: 'deposit/ask-nothing-breaks',
    section: 'askVela',
    scenes: [VelaScene.Deposit],
    priority: 60,
    evaluate: (ctx, t) => {
      const cover = coverKind(ctx);
      if (!cover || hasDepositFee(ctx)) return null;
      return {
        id: 'ask-nothing-breaks',
        title: t('chat.deposit.nothing_breaks.q'),
        description: t(NOTHING_BREAKS[cover][depositKind(ctx)], amounts(ctx)),
      };
    },
  },
  {
    id: 'deposit/ask-fee',
    section: 'askVela',
    scenes: [VelaScene.Deposit],
    priority: 60,
    evaluate: (ctx, t) =>
      hasDepositFee(ctx)
        ? {
            id: 'ask-fee',
            title: t('chat.deposit.fee.q', amounts(ctx)),
            description: t(FEE[depositKind(ctx)], amounts(ctx)),
          }
        : null,
  },
];
